import React, { useRef } from 'react';
import { Target, Compass } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { aboutData } from '../mock/data';

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <section id="about" className="py-12 sm:py-16 md:py-24 bg-white relative overflow-hidden">
      {/* Futuristic grid background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(to right, #1e293b 1px, transparent 1px),
            linear-gradient(to bottom, #1e293b 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 px-1"
          >
            ABOUT <span className="text-blue-600">US</span>
          </motion.h2>
          <motion.div
            initial={{ width: 0 }}
            animate={isInView ? { width: 80 } : { width: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="h-1 bg-blue-600 mx-auto rounded-full"
          ></motion.div>
        </motion.div>

        {/* Main Description */}
        <div className="mb-16">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-base sm:text-lg text-slate-700 leading-relaxed max-w-5xl mx-auto text-justify hyphens-auto"
          >
            {aboutData.description}
          </motion.p>
        </div>

        {/* Vision & Mission - Corporate Style */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Vision */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="group relative bg-gradient-to-br from-slate-50 to-blue-50/30 p-6 sm:p-8 rounded-2xl border border-slate-200 hover:border-blue-300 transition-all duration-300 hover:shadow-xl"
          >
            <div className="flex items-center mb-6">
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
                className="w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg"
              >
                <Target className="w-7 h-7 text-white" />
              </motion.div>
              <h3 className="text-2xl font-bold text-slate-900 ml-4">
                Our Vision
              </h3>
            </div>
            <p className="text-slate-700 leading-relaxed text-justify hyphens-auto">
              {aboutData.vision}
            </p>

            {/* Corner decoration */}
            <div className="absolute top-0 right-0 w-20 h-20 border-t-2 border-r-2 border-blue-400/30 rounded-tr-2xl"></div>
          </motion.div>

          {/* Mission */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="group relative bg-gradient-to-br from-slate-50 to-indigo-50/30 p-6 sm:p-8 rounded-2xl border border-slate-200 hover:border-indigo-300 transition-all duration-300 hover:shadow-xl"
          >
            <div className="flex items-center mb-6">
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
                className="w-14 h-14 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg"
              >
                <Compass className="w-7 h-7 text-white" />
              </motion.div>
              <h3 className="text-2xl font-bold text-slate-900 ml-4">
                Our Mission
              </h3>
            </div>
            <p className="text-slate-700 leading-relaxed text-justify hyphens-auto">
              {aboutData.mission}
            </p>

            {/* Corner decoration */}
            <div className="absolute bottom-0 left-0 w-20 h-20 border-b-2 border-l-2 border-indigo-400/30 rounded-bl-2xl"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
